"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import {
  clearSession,
  getSession,
  isPhotoBoothKioskActive,
  PHOTO_BOOTH_KIOSK_CHANGE_EVENT,
  type PortalSession,
} from "@/app/lib/weddingPortal";

function getDashboardHref(session: PortalSession) {
  return session.role === "superadmin" ? "/super" : "/dashboard";
}

export default function SessionActionBar() {
  const pathname = usePathname();
  const router = useRouter();
  const [session, setSession] = useState<PortalSession | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return getSession();
  });
  const [kioskActive, setKioskActive] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return isPhotoBoothKioskActive();
  });

  useEffect(() => {
    function handleStorage() {
      setSession(getSession());
      setKioskActive(isPhotoBoothKioskActive());
    }

    function handleKioskChange() {
      setKioskActive(isPhotoBoothKioskActive());
    }

    window.addEventListener("storage", handleStorage);
    window.addEventListener(PHOTO_BOOTH_KIOSK_CHANGE_EVENT, handleKioskChange);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(PHOTO_BOOTH_KIOSK_CHANGE_EVENT, handleKioskChange);
    };
  }, []);

  if (!session) return null;
  if (kioskActive || pathname === "/login" || pathname === "/booth") return null;

  const dashboardHref = getDashboardHref(session);
  const isDashboardHome = pathname === dashboardHref;
  const showPublicSiteLink = session.role === "superadmin" && pathname.startsWith("/super");
  const dashboardLabel = session.role === "superadmin" ? "Super Dashboard" : "Dashboard";
  const publicSiteLabel =
    session.role === "superadmin" ? "View Public Site" : "Home";

  function logout() {
    clearSession();
    router.push("/login?logged_out=1");
  }

  return (
    <div className="fixed right-4 top-4 z-40 flex flex-wrap items-center justify-end gap-2 sm:right-6 sm:top-6">
      {pathname !== "/" && !showPublicSiteLink ? (
        <Link
          href="/"
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white/95 px-4 py-2 text-sm font-semibold uppercase tracking-[.12em] text-[#1f4e79] shadow-[0_12px_26px_rgba(31,78,121,0.10)] backdrop-blur"
        >
          {publicSiteLabel}
        </Link>
      ) : null}

      {showPublicSiteLink ? (
        <Link
          href="/"
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white/95 px-4 py-2 text-sm font-semibold uppercase tracking-[.12em] text-[#1f4e79] shadow-[0_12px_26px_rgba(31,78,121,0.10)] backdrop-blur"
        >
          View Public Site
        </Link>
      ) : null}

      {!isDashboardHome ? (
        <Link
          href={dashboardHref}
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white/95 px-4 py-2 text-sm font-semibold uppercase tracking-[.12em] text-[#1f4e79] shadow-[0_12px_26px_rgba(31,78,121,0.10)] backdrop-blur"
        >
          {dashboardLabel}
        </Link>
      ) : null}

      <button
        type="button"
        onClick={logout}
        className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#1f4e79] px-4 py-2 text-sm font-semibold uppercase tracking-[.12em] text-white shadow-[0_12px_26px_rgba(31,78,121,0.14)]"
      >
        Logout
      </button>
    </div>
  );
}
